import {Comment, getMarkdownComments, useAppSelector} from "@dv5/domain"
import {ErrorProcessor, userInfoApi} from "@dv5/api"
import React, {FC, useEffect, useRef, useState} from "react"
import {CommentItem} from "./CommentItem"
import {Icon, IconName} from "../buttons"
import {Button} from "../inputs/Button"
import {MultilineEditor} from "../inputs/multiline"
import './CommentDialog.less'

interface Props {
    isOpen: boolean
    sourceItemId: string
    comments: Array<Comment>
    onGetComment: (sourceItemId: string) => void
    onSend: (content: string) => void
    onEdit: (commentId: string, content: string) => void
    onDelete: (commentId: string) => void
    onClose: () => void
    markdown?: boolean
}

export const normalizeCommentDraft = (content: string): string | null => {
    const trimmed = content.trim()
    return trimmed.length > 0 ? trimmed : null
}

export const isCommentDraftEligible = (content: string): boolean => normalizeCommentDraft(content) !== null

export type CommentViewMode = 'compact' | 'normal'

export const getNextCommentViewMode = (viewMode: CommentViewMode): CommentViewMode =>
    viewMode === 'compact' ? 'normal' : 'compact'

export const CommentDialog: FC<Props> = ({
    isOpen,
    sourceItemId,
    comments,
    onGetComment,
    onSend,
    onEdit,
    onDelete,
    onClose,
    markdown: markdownProp,
}) => {
    const globalMarkdownComments = useAppSelector(getMarkdownComments)
    const markdown = markdownProp ?? globalMarkdownComments
    const [content, setContent] = useState('')
    const [viewMode, setViewMode] = useState<CommentViewMode>('compact')
    const dialogRef = useRef<HTMLDivElement>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            onGetComment(sourceItemId)
        }
    }, [loaded])

    useEffect(() => {
        setLoaded(false)
    }, [sourceItemId])

    const [userNames, setUserNames] = useState<Record<string, string>>({})

    useEffect(() => {
        const authorIds = [...new Set(comments.map(c => c.authorId).filter(Boolean))]
        if (authorIds.length === 0) return
        const noop: ErrorProcessor = {displayError: () => {}, displayStep: () => {}}
        userInfoApi.getMultipleUserInfo(authorIds, noop, (users) => {
            const map: Record<string, string> = {}
            users.forEach(u => { map[u.id] = u.username })
            setUserNames(map)
        })
    }, [comments])

    if (!isOpen) {
        return null
    }

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose()
        }
    }

    const handleSend = () => {
        const draft = normalizeCommentDraft(content)
        if (!draft) return
        onSend(draft)
        setContent('')
    }

    const canAddComment = isCommentDraftEligible(content)

    const handleCancel = () => {
        setContent('')
        onClose()
    }

    const isCompact = viewMode === 'compact'
    const viewModeActionLabel = isCompact ? 'Switch to normal view' : 'Switch to compact view'

    return (
        <div className="comment-dialog-overlay" onClick={handleOverlayClick}>
            <div
                className={`comment-dialog comment-dialog--${viewMode}`}
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="comment-dialog-title"
            >
                <div className="comment-dialog-header">
                    <h3 id="comment-dialog-title">Comments</h3>
                    <button
                        type="button"
                        className="comment-view-mode-button"
                        onClick={() => setViewMode(getNextCommentViewMode(viewMode))}
                        aria-label={viewModeActionLabel}
                        aria-pressed={isCompact}
                        title={viewModeActionLabel}
                    >
                        <Icon name={isCompact ? IconName.Expand : IconName.Collapse} width="18" height="18"/>
                    </button>
                </div>
                {comments.length > 0 && (
                    <div className="comment-dialog-comments-list">
                        {comments.map((comment) => (
                            <CommentItem
                                key={comment.id}
                                comment={comment}
                                userName={userNames[comment.authorId] || comment.authorId}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                compact={isCompact}
                                markdown={markdown}
                            />
                        ))}
                    </div>
                )}
                <div className="editor-container">
                    <div className="compose-guidance">Write a comment</div>
                    <MultilineEditor
                        value={content}
                        setValue={setContent}
                        onConfirm={handleSend}
                        onCancel={handleCancel}
                        settings={{autoFocus: true, autoResize: true}}
                        disabled={false}
                    />
                </div>
                <div className="buttons">
                    <Button title="Cancel" onClick={handleCancel} className="cancel-dialog-button" />
                    <Button title="Add comment" onClick={handleSend} className="send-dialog-button" disabled={!canAddComment} />
                </div>
            </div>
        </div>
    )
}
